/** PFM (Portable Float Map) and PGM/PPM (P2, P3, P5, P6; 8 or 16 bit) decoders. */
import type { RawFrame } from '../../core/types';
import { defaultHint, flipRows, makeFrame } from '../frame';

/** Tokenise a Netpbm header, skipping comments; returns tokens and the offset after the last one + 1 whitespace. */
function header(b: Uint8Array, count: number): { tokens: string[]; offset: number } {
  const tokens: string[] = [];
  let o = 0;
  while (tokens.length < count && o < b.length) {
    const c = b[o];
    if (c === 0x23) {
      while (o < b.length && b[o] !== 0x0a && b[o] !== 0x0d) o++;
      continue;
    }
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      o++;
      continue;
    }
    let s = '';
    while (o < b.length && !/\s/.test(String.fromCharCode(b[o]))) s += String.fromCharCode(b[o++]);
    tokens.push(s);
  }
  if (b[o] === 0x0d && b[o + 1] === 0x0a) o += 2;
  else o += 1; // single whitespace after the last header token
  return { tokens, offset: o };
}

export function decodePfm(bytes: Uint8Array, name: string): RawFrame {
  const { tokens, offset } = header(bytes, 4);
  const [magic, ws, hs, ss] = tokens;
  const channels = magic === 'PF' ? 3 : magic === 'Pf' ? 1 : 0;
  if (!channels) throw new Error('Not a PFM file');
  const width = parseInt(ws, 10);
  const height = parseInt(hs, 10);
  const scale = parseFloat(ss);
  const le = scale < 0;
  const n = width * height * channels;
  if (offset + n * 4 > bytes.length) throw new Error('PFM file is truncated');
  const dv = new DataView(bytes.buffer, bytes.byteOffset + offset, n * 4);
  const data = new Float32Array(n);
  for (let i = 0; i < n; i++) data[i] = dv.getFloat32(i * 4, le);
  flipRows(data, width, height, channels); // PFM stores rows bottom-to-top
  return makeFrame({
    width,
    height,
    data,
    channels,
    isFloat: true,
    bitDepth: 32,
    format: 'pfm',
    name,
    encoding: { ...defaultHint('PFM stores linear floating-point values', 'linear'), source: 'format' },
    info: { Scale: `${Math.abs(scale)}`, Endianness: le ? 'little' : 'big' },
  });
}

export function decodePnm(bytes: Uint8Array, name: string): RawFrame {
  const magic = String.fromCharCode(bytes[0], bytes[1]);
  const isBitmap = magic === 'P1' || magic === 'P4';
  if (isBitmap) throw new Error('PBM (1-bit) files are not supported');
  const { tokens, offset } = header(bytes, 4);
  const width = parseInt(tokens[1], 10);
  const height = parseInt(tokens[2], 10);
  const maxval = parseInt(tokens[3], 10);
  const channels = magic === 'P3' || magic === 'P6' ? 3 : 1;
  const n = width * height * channels;
  const wide = maxval > 255;
  const data = wide ? new Uint16Array(n) : new Uint8Array(n);
  if (magic === 'P5' || magic === 'P6') {
    if (wide) {
      const dv = new DataView(bytes.buffer, bytes.byteOffset + offset, n * 2);
      for (let i = 0; i < n; i++) data[i] = dv.getUint16(i * 2, false);
    } else data.set(bytes.subarray(offset, offset + n));
  } else {
    const text = new TextDecoder('latin1').decode(bytes.subarray(offset));
    const vals = text
      .replace(/#[^\n]*/g, ' ')
      .trim()
      .split(/\s+/);
    for (let i = 0; i < n; i++) data[i] = parseInt(vals[i], 10);
  }
  return makeFrame({
    width,
    height,
    data,
    channels,
    maxCode: maxval,
    bitDepth: Math.ceil(Math.log2(maxval + 1)),
    format: 'pnm',
    name,
    encoding: wide
      ? defaultHint('16-bit Netpbm without colour metadata: assuming linear (e.g. dcraw -4 output)', 'linear')
      : defaultHint('8-bit Netpbm: assuming sRGB'),
    info: { Type: magic, Maxval: `${maxval}` },
  });
}
